import * as React from 'react';
import styled, {css} from 'styled-components';
import {lighten, transparentize} from 'polished';
import outline from 'helpers/outline';
import {transition} from 'helpers/css';
import {Theme} from '../stores/AppStore';

export enum ControlButtonVariant {
  Default = 'default',
  Accent = 'accent',
}

export enum ControlButtonScale {
  Default = 'default',
  Small = 'small',
}

export interface ControlButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ControlButtonVariant;
  scale?: ControlButtonScale;
}

export const Container = styled.button<ControlButtonProps>`
  justify-content: center;
  align-items: center;
  display: flex;
  
  border-radius: 50%;
  border: 2px solid transparent;
  color: ${({theme}) => transparentize(.6, theme.variables.color)};
  
  ${({scale}) => {
    switch (scale) {
      case ControlButtonScale.Default: return css`
        width: 35px;
        height: 35px;
      `;
      case ControlButtonScale.Small: return css`
        width: 30px;
        height: 30px;
      `;
    }
  }}
  
  ${({variant}) => {
    switch (variant) {
      case ControlButtonVariant.Default: return css`
        color: ${({theme}) => theme.name === Theme.Dark ? `
          ${transparentize(.6, theme.colors.white)};
        ` : (
          transparentize(.6, theme.variables.color)
        )};
        
        &:hover,
        &:active {
          color: ${({theme}) => theme.name === Theme.Dark ? `
            ${theme.colors.white};
          ` : (
            transparentize(.3, theme.variables.color)
          )};
          background: ${({theme}) => theme.name === Theme.Dark ? `
            ${transparentize(.9, theme.colors.white)};
          ` : (
            transparentize(.9, theme.variables.color)
          )};
        }
        
        &:active {
          border: 
            2px 
            solid
            ${({theme}) => transparentize(.6, theme.colors.primary)};
        }
      `;
      case ControlButtonVariant.Accent: return css`
        color: ${({theme}) => `
          ${transparentize(.6, theme.colors.white)};
        `};
        
        background: ${({theme}) => `
          ${theme.variables.accentColor};
        `};
        
        &:hover {
          color: ${({theme}) => `
            ${theme.colors.white};
          `};
        }
        
        &:active {
          background: ${({theme}) => `
            ${lighten(.08, theme.variables.accentColor)};
          `};
        }
      `;
    }
  }};
  
  ${transition()};
  
  &:focus-visible {
    ${outline(6, 1)};
  }
`;

/**
 * The control button with icon
 * @param {ControlButtonProps} props
 * @return {React.ReactElement<ControlButtonProps>}
 * @constructor
 */
function ControlButton(
    props: ControlButtonProps,
): React.ReactElement<ControlButtonProps> {
  return <Container {...props} />;
}

ControlButton.defaultProps = {
  variant: ControlButtonVariant.Default,
  scale: ControlButtonScale.Default,
};

export default ControlButton;
